import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import CategoriesApp, { ICategoryModel } from './CategoriesApp'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { addNewCategory, updateAllCategories, updateCategoriesLoadingStatus, updateCategoriesPagination, updateCategory } from './categoriesSlice'
import { ISearchParam } from '../BaseApp'
import { handleRequestError } from '../../services/api'
import NothingFound from '../../components/NothingFound'
import SingleCategory from './SingleCategory'
import { LoadingButton } from '@mui/lab'
import GenerateIcon from '../../components/GenerateIcon'
import BasicModalWindow from '../../components/BasicModalWIndow'
import { Alert, Button, Skeleton, TextField } from '@mui/material'
import Grid from '@mui/material/Grid2';
import { IAxiosDefaultDataObject } from '../../config'
import { error } from 'console'
import { useReturnCurrentUser } from '../user/userSlice'
import DynamicModelSelect from '../../components/Fields/DynamicModelSelect'
import CategorySelectFieldElement from '../../components/Fields/CategorySelectFieldElement'

interface ICategoriesListProps {
    handleCategorySelect?: (category: ICategoryModel) => void,
    enableSelectCategory?: boolean,
    disableAddNew?: boolean,
    checkedCategories?: number[],
    updateCheckedCategories?: (categories: number[]) => void,
    loadAll? : boolean,
    enableGroups?: boolean,
    customNewCategoryButtonRef?: React.MutableRefObject<HTMLButtonElement | null>
}

export default function CategoriesList(props: ICategoriesListProps) {

    const dispatch = useDispatch()

    const categoriesApp = new CategoriesApp()

    const {
        handleCategorySelect,
        enableSelectCategory,
        checkedCategories,
        updateCheckedCategories,
        loadAll,
        enableGroups,
        customNewCategoryButtonRef,
    } = props

    const { 
        categories,
        pagination,
        loading,
        selectedCategories,
        search,
        searchParams,
    } = useSelector((state: RootState) => state.categories)

    const [showCategoryModal, setShowCategoryModal] = useState<boolean>(false)
    const [category, setCategory] = useState<ICategoryModel | null>(null)
    const [parentCategory, setParentCategory] = useState<number | null>(null)
    const [categoryTitle, setCategoryTitle] = useState<string>("")
    const [categoryParentId, setCategoryParentId] = useState<number | null>(null)

    const [loadingRequest, setLoadingRequest] = useState<boolean>(false)
    const [disableAddNew, setDisableAddNew] = useState<boolean>(props.disableAddNew ? props.disableAddNew : false)
    
    const currentUser = useReturnCurrentUser()

    const fallbackRef = useRef<HTMLButtonElement | null>(null);
    const newCategoryButtonRef = customNewCategoryButtonRef || fallbackRef;

    /**
     * show modal
     */
    const handleShowModal = () => {
        setShowCategoryModal(true)
    }

    /**
     * hide modal
     */
    const handleHideModal = () => {
        setShowCategoryModal(false)
        setCategory(null)
        setParentCategory(null)
        setCategoryTitle("")
        setCategoryParentId(null)
    }

    /**
     * Enbale create button
     */
    const enableCreateButton = () => {
        if (loadingRequest){
            return true
        }

        if (!categoryTitle){
            return true
        }

        return false
    }

    /**
     * Handle create or update category
     */
    const handleUpdateOrCreateCategory = () => {

        let data: IAxiosDefaultDataObject = {}

        if (category){
            data = {
                ...category,
                update: true
            }
        }

        if (parentCategory){
            data.category_id = parentCategory
        }

        if (categoryParentId){
            data.category_id = categoryParentId
            data.is_sub_category = true
        }else{
            if (category){
                data.category_id = null
                data.is_sub_category = false
            }
        }

        data.title = categoryTitle

        setLoadingRequest(true)

        categoriesApp.createNewInstance(data)
            .then(response => {

                const createdCategory: ICategoryModel = response.data

                if (!category) {
                    dispatch(addNewCategory(createdCategory)); // For new items
                } else {
                    if (category.category_id !== createdCategory.category_id){
                        loadCategories(1, search ?? "", false, false, searchParams)
                    }

                    dispatch(updateCategory(createdCategory)); // For updates
                }

                setLoadingRequest(false)
                handleHideModal()
            })
            .catch(error => {
                handleRequestError(error, dispatch)
                setLoadingRequest(false)
            })

    }

    /**
     * Update loading status
     */
    const updateLoadingStatus = (status: boolean) => dispatch(updateCategoriesLoadingStatus({ loading: status }))

    /**
     * Load categories
     */
    const loadCategories = (page: number, search: string|null, disablePagination: boolean, addItems?: boolean, searchParams?: ISearchParam[]) => {
        updateLoadingStatus(true)
        categoriesApp.listOfInstances(page, search, disablePagination, addItems, searchParams, enableGroups)
            .then(response => {

                const { data } = response


                if (disablePagination){

                    dispatch(updateAllCategories(
                        data
                    ))

                    dispatch(updateCategoriesPagination({
                        count: 1,
                        current_page: 1,
                        total_pages: 1
                    }))
                }else{
                    const results: ICategoryModel[] = data.results

                    if (addItems){
                        dispatch(updateAllCategories([
                            ...categories,
                            ...results
                        ]))
                    }else{
                        dispatch(updateAllCategories([]))
                        dispatch(updateAllCategories(results))
                    }
            
                    dispatch(updateCategoriesPagination({
                        count: data.count,
                        current_page: page,
                        total_pages: data.total_pages
                    }))
                }

                updateLoadingStatus(false)

            })
            .catch(error => {
                handleRequestError(error, dispatch)
                updateLoadingStatus(false)
            })
    }

    /**
     * Load categories
     */
    useEffect(() => {
        loadCategories(1, search ? search : "", loadAll ? true : false, false, searchParams)
    }, [])

    useEffect(() => {
        if (category){
            setCategoryTitle(category.title)
            setCategoryParentId(category.category_id)
        }
    }, [category])

    useEffect(() => {
        if (props.disableAddNew){
            setDisableAddNew(props.disableAddNew)
        }

        if (!currentUser.is_superuser || !currentUser.is_daemon){
            setDisableAddNew(true)
        }

    }, [currentUser, props.disableAddNew])

    /**
     * Render load more
     */
    const renderMore = () => {
        if (pagination.total_pages === pagination.current_page){
            return <></>
        }

        return(
            <div className="load-more-categories">
                <div className="content-of-load-more-categories">
                    <LoadingButton variant="contained" className="general-button admin-style" loading={loading} startIcon={<GenerateIcon icon="refresh" />} onClick={() => loadCategories(pagination.current_page + 1, search ? search : "", false, true, searchParams)}>
                        Load more
                    </LoadingButton>
                </div>
            </div>
        )
    }

    /**
     * No categories found
     */
    if (!loading && categories.length === 0){
        return <NothingFound message={categoriesApp.messages.nothingFound} />
    }

    const renderLoading = () => {
        if (loading){
            return(
                <div>
                    <Skeleton variant="rectangular" width="100%" height={50} style={{ marginBottom: 10 }} />
                    <Skeleton variant="rectangular" width="100%" height={50} style={{ marginBottom: 10 }} />
                    <Skeleton variant="rectangular" width="100%" height={50} style={{ marginBottom: 10 }} />
                    <Skeleton variant="rectangular" width="100%" height={50} style={{ marginBottom: 10 }} />
                    <Skeleton variant="rectangular" width="100%" height={50} style={{ marginBottom: 10 }} />
                </div>
            )
        }

        return <></>
    }

    return(
        <div className="categories-container-wrapper">
            <div className="content-of-categories-container-wrapper">

                <BasicModalWindow
                    shown={showCategoryModal}
                    handleClose={handleHideModal}
                    showHeader
                    title={category ? "Edit " + category.title : parentCategory ? "New sub category" : "New category"}
                    actionButtons={[
                        {
                            label: category ? "Update" : "Create",
                            onClick: handleUpdateOrCreateCategory,
                            color: "success",
                            disabled: enableCreateButton() ? true : false
                        },
                        {
                            label: "Close",
                            onClick: handleHideModal,
                            color: "error"
                        }
                    ]}
                    actionButtonsBottom
                    size="sm"
                >
                    <Grid container spacing={2}>

                        <Grid size={{ xs: 12 }}>
                            <TextField 
                                variant="outlined"
                                label="Title"
                                fullWidth
                                value={categoryTitle}
                                onChange={(e) => setCategoryTitle(e.target.value)}
                            />
                        </Grid>

                        {category && (
                            <Grid size={{ xs: 12 }}>
                                <CategorySelectFieldElement
                                    id="category_id"
                                    type="DynamicModelSelect"
                                    xs={12} 
                                    variant="outlined"
                                    label="Parent Category"
                                    fullWidth
                                    data={{
                                        name: "category_id",
                                        value: categoryParentId
                                    }}
                                    handleOnChange={(id, value) => setCategoryParentId(value ? value : null)}
                                    appClass={new CategoriesApp}
                                />
                            </Grid>
                        )}

                    </Grid>
                </BasicModalWindow>

                <div className="categories-list-container">
                    <div className="content-of-categories-list-container">

                        {handleCategorySelect && (
                            <div style={{ marginBottom: 15 }}>
                                <Alert color="info" icon={<></>}>
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        Click <div style={{ marginTop: 3, marginLeft: 5, marginRight: 5 }}><GenerateIcon icon="topic" /></div> to see sub categories. Click category title to select it
                                    </div>
                                </Alert>
                            </div>
                        )}

                        {categories.map((category, index: number) => <SingleCategory {...category} disableAddNew={disableAddNew} enableSelect={enableSelectCategory} checkedCategories={checkedCategories} updateCheckedCategories={updateCheckedCategories} key={index} handleShowModal={handleShowModal} handleHideModal={handleHideModal} updateModalCategory={(category) => setCategory(category)} updateParentCategoryId={setParentCategory} handleCategorySelect={handleCategorySelect} />)}
                        {renderLoading()}

                        {!disableAddNew && (
                            <div className="new-sub-category-button-box list">
                                <div className="content-of-new-sub-category-button-box">
                                    <Button ref={newCategoryButtonRef} className="general-button admin-style" startIcon={<GenerateIcon icon="new" />} onClick={() => handleShowModal()}>
                                        New category
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {renderMore()}

            </div>
        </div>
    )
}